/**
 * Benepass transfer reporting API — read-only admin reports.
 */

import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';

export interface TransferStatusCount {
  status: string;
  count: number;
  total_amount: string;
}

export interface TransferSummaryResponse {
  counts: TransferStatusCount[];
  total_count: number;
  total_amount: string;
}

export interface PartnerProductBreakdownItem {
  partner_product_id: number | null;
  partner_name: string | null;
  doing_business_as: string | null;
  counts: TransferStatusCount[];
  total_count: number;
  total_amount: string;
}

export interface PartnerProductBreakdownResponse {
  items: PartnerProductBreakdownItem[];
}

export interface BenepassTransferListItem {
  id: number;
  reference_number: string;
  eligible_participant_id: number;
  health_plan_code: string;
  program_code: string;
  purse_name: string;
  service_date: string;
  reward_code: string;
  reward_amount: string;
  status: string;
  skip_reason: string | null;
  created_at: string;
  sent_to_incomm_at: string | null;
  sent_to_incomm_filename: string | null;
  incomm_processed_at: string | null;
  incomm_processed_status: string | null;
  incomm_response_message: string | null;
  partner_product_id: number | null;
  partner_name: string | null;
  doing_business_as: string | null;
}

export interface BenepassTransferListResponse {
  items: BenepassTransferListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface TransferListParams {
  page: number;
  page_size: number;
  eligible_participant_id?: number;
  status?: string;
  date_from?: string;
  date_to?: string;
  partner_product_id?: number;
}

export async function fetchTransferSummary(): Promise<TransferSummaryResponse> {
  try {
    const response = await api.get<TransferSummaryResponse>(
      API_ENDPOINTS.admin.benepassTransfersSummary
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Failed to fetch Benepass transfer summary:', error);
    throw error;
  }
}

export async function fetchPartnerProductBreakdown(): Promise<PartnerProductBreakdownResponse> {
  try {
    const response = await api.get<PartnerProductBreakdownResponse>(
      API_ENDPOINTS.admin.benepassTransfersSummaryByPartner
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Failed to fetch Benepass partner breakdown:', error);
    throw error;
  }
}

export async function fetchTransferList(
  params: TransferListParams
): Promise<BenepassTransferListResponse> {
  try {
    const response = await api.get<BenepassTransferListResponse>(
      API_ENDPOINTS.admin.benepassTransfers,
      { params }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Failed to fetch Benepass transfer list:', error);
    throw error;
  }
}
